/**
 * @element qds-image-helper
 * 
 * A component for displaying images with download and copy functionality.
 * Helps designers and developers use images consistently by providing easy access
 * to different formats and download options.
 * 
 * @attr {string} src - The source URL of the image
 * @attr {string} headline - The headline text
 * @attr {string} subline - The subline text
 * @attr {string} description - The description text
 * @attr {string} backgroundcolor - The background color of the image container
 * 
 * @csspart container - The main container element
 * @csspart image-container - The image container element
 * @csspart info-card - The information section element
 * @csspart button-container - The container for the action buttons
 */

import { loadBaseCSS } from '../shared/button-utils.js';

// Load base CSS
loadBaseCSS();

export class QdsImageHelper extends HTMLElement {
  private imageElement!: HTMLImageElement;
  private downloadButton!: HTMLButtonElement;
  private copyPngButton!: HTMLButtonElement;
  private copyLinkButton!: HTMLButtonElement;
  private notificationElement!: HTMLElement;
  private _notificationTimeout: number | null = null;
  private readonly _handleDownloadClick = (): void => {
    void this.downloadImage();
  };
  private readonly _handleCopyPngClick = (): void => {
    this.copyAsPng();
  };
  private readonly _handleCopyLinkClick = (): void => {
    this.copyLink();
  };

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    this.render();
  }

  connectedCallback(): void {
    if (!this.isConnected) return;

    this.downloadButton.addEventListener('click', this._handleDownloadClick);
    this.copyPngButton.addEventListener('click', this._handleCopyPngClick);
    this.copyLinkButton.addEventListener('click', this._handleCopyLinkClick);
  }

  disconnectedCallback(): void {
    this.downloadButton.removeEventListener('click', this._handleDownloadClick);
    this.copyPngButton.removeEventListener('click', this._handleCopyPngClick);
    this.copyLinkButton.removeEventListener('click', this._handleCopyLinkClick);

    if (this._notificationTimeout !== null) {
      window.clearTimeout(this._notificationTimeout);
      this._notificationTimeout = null;
    }
  }

  private render(): void {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        @import '/dev/assets/css/q-base.css';

        :host {
          --_image-helper-radius: var(--theme-radius-md, 8px);
          --_image-helper-radius-sm: var(--theme-radius-sm, 4px);
          --_image-helper-surface: var(--theme-background-primary);
          --_image-helper-surface-subtle: var(--theme-background-secondary);
          --_image-helper-preview-background: var(--theme-background-primary);
          --_image-helper-border: var(--theme-border-primary);
          --_image-helper-border-strong: var(--theme-border-strong);
          --_image-helper-shadow: var(--theme-shadow-sm);
          --_image-helper-title: var(--theme-text-primary);
          --_image-helper-subtle-text: var(--theme-text-secondary);
          --_image-helper-body-text: var(--theme-text-primary);
          --_image-helper-action: var(--theme-accent-primary);
          --_image-helper-action-hover: var(--theme-background-hover);
          --_image-helper-notification-background: var(--theme-success);
          --_image-helper-notification-color: var(--theme-text-on-accent-primary);
          display: block;
          color: var(--_image-helper-body-text);
          font-family: var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
        }

        .logo-component-container {
          border-radius: var(--_image-helper-radius);
          box-shadow: var(--_image-helper-shadow);
          background-color: var(--_image-helper-surface);
          display: flex;
          width: 100%;
          max-width: 582px;
          flex-direction: column;
          border: 1px solid var(--_image-helper-border);
          overflow: hidden;
        }

        .logo-container {
          align-items: center;
          background-color: var(--_image-helper-preview-background);
          display: flex;
          justify-content: center;
          padding: clamp(32px, 8vw, 64px);
        }

        .logo {
          display: block;
          object-position: center;
          width: 100%;
          max-width: 455px;
          height: auto;
        }

        .info-card {
          background-color: var(--_image-helper-surface);
          display: flex;
          flex-direction: column;
          gap: var(--theme-space-2, 8px);
          font-size: var(--theme-font-size-sm, 14px);
          color: var(--_image-helper-body-text);
          font-weight: var(--theme-font-weight-normal, 400);
          padding: var(--theme-space-6, 24px);
          border-top: 1px solid var(--_image-helper-border);
        }

        .headline {
          margin: 0;
          color: var(--_image-helper-title);
          font-size: 21px;
          font-weight: 700;
          line-height: 1.2;
        }

        .subline {
          color: var(--_image-helper-subtle-text);
          line-height: 150%;
          margin: 0;
        }

        .description {
          font-size: 1rem;
          line-height: 1.75;
          margin-top: var(--theme-space-4, 16px);
        }

        .button-container {
          display: flex;
          margin-top: var(--theme-space-4, 16px);
          gap: var(--theme-space-3, 12px);
          flex-wrap: wrap;
        }

        .button-outlined {
          appearance: none;
          background: transparent;
          border: 1px solid var(--_image-helper-border-strong);
          border-radius: var(--_image-helper-radius-sm);
          color: var(--_image-helper-action);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 44px;
          padding: 12px 18px;
          font: 600 var(--theme-font-size-sm, 14px) var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif));
          flex: 1 1 160px;
          text-align: center;
          cursor: pointer;
          transition:
            background-color 0.2s ease,
            border-color 0.2s ease,
            color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .button-outlined:hover {
          background-color: var(--_image-helper-action-hover);
        }

        .button-outlined:focus-visible {
          outline: 2px solid var(--_image-helper-action);
          outline-offset: 2px;
          box-shadow: 0 0 0 4px var(--button-focus-ring);
        }

        .notification {
          position: fixed;
          top: var(--theme-space-5, 20px);
          right: var(--theme-space-5, 20px);
          background-color: var(--_image-helper-notification-background);
          color: var(--_image-helper-notification-color);
          padding: 10px 20px;
          border-radius: var(--_image-helper-radius-sm);
          display: none;
          z-index: 1000;
          font-size: var(--theme-font-size-md, 16px);
          box-shadow: var(--_image-helper-shadow);
        }
      </style>

      <div class="logo-component-container" part="container">
        <div class="logo-container" part="image-container">
          <img id="image" alt="SVG Image" src="https://cdn.q360.ai/qed-design/QuantumLogos/Quilt/quilt-default-logo-blue.svg" class="logo" width="auto" height="128"/>
        </div>
        <div class="info-card" part="info-card">
          <div class="headline">Headline</div>
          <div class="subline"></div>
          <div class="description">
            Use <b>PNG</b> for presentations materials, i.e. PowerPoint file.
            Use <b>SVG</b> on websites and applications
          </div>
          <div class="button-container" part="button-container">
            <button id="download-btn" class="button-outlined" type="button">Download</button>
            <button id="copy-png-btn" class="button-outlined" type="button">Copy as PNG</button>
            <button id="copy-link-btn" class="button-outlined" type="button">Copy Link</button>
          </div>
        </div>
      </div>
      <div id="notification" class="notification" role="status" aria-live="polite"></div>
    `;

    this.imageElement = this.shadowRoot.querySelector('#image') as HTMLImageElement;
    this.downloadButton = this.shadowRoot.querySelector('#download-btn') as HTMLButtonElement;
    this.copyPngButton = this.shadowRoot.querySelector('#copy-png-btn') as HTMLButtonElement;
    this.copyLinkButton = this.shadowRoot.querySelector('#copy-link-btn') as HTMLButtonElement;
    this.notificationElement = this.shadowRoot.querySelector('#notification') as HTMLElement;
  }

  /**
   * Extract the filename from a URL
   * @param url The URL to extract the filename from
   * @returns The filename
   */
  private static getFileName(url: string): string {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1);
    return fileName;
  }

  static get observedAttributes(): string[] {
    return [
      'src',
      'headline',
      'subline',
      'description',
      'backgroundcolor'
    ];
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    if (name === 'src') {
      this.imageElement.src = newValue || '';
    } else if (name === 'headline') {
      const headline = this.shadowRoot!.querySelector('.headline');
      if (headline) headline.textContent = newValue || '';
    } else if (name === 'subline') {
      const subline = this.shadowRoot!.querySelector('.subline');
      if (subline) subline.textContent = newValue || '';
    } else if (name === 'description') {
      const description = this.shadowRoot!.querySelector('.description');
      if (description) description.textContent = newValue || '';
    } else if (name === 'backgroundcolor') {
      if (newValue) {
        this.style.setProperty('--_image-helper-preview-background', newValue);
      } else {
        this.style.removeProperty('--_image-helper-preview-background');
      }
    }
  }

  /**
   * Download the image
   */
  public async downloadImage(): Promise<void> {
    try {
      // Fetch the image data
      const response = await fetch(this.imageElement.src, { mode: 'cors' });
              
      if (!response.ok) {
        throw new Error('Network response was not ok ' + response.statusText);
      }

      // Convert the response to a blob
      const blob = await response.blob();

      // Create a temporary URL for the blob
      const url = URL.createObjectURL(blob);        
      const link = document.createElement('a');
      link.href = url;
      link.download = QdsImageHelper.getFileName(this.imageElement.src);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
      this.showNotification('Error downloading image');
    }
  }

  /**
   * Copy the image as PNG to clipboard
   */
  public copyAsPng(): void {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.showNotification('Error: Could not create canvas context');
      return;
    }
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = this.imageElement.src;
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        if (!blob) {
          this.showNotification('Error: Could not create image blob');
          return;
        }
        
        const item = new ClipboardItem({ 'image/png': blob });
        navigator.clipboard.write([item]).then(() => {
          this.showNotification('Image copied as PNG');
        }).catch(err => {
          console.error('Error copying to clipboard:', err);
          this.showNotification('Error copying to clipboard');
        });
      });
    };
    
    img.onerror = () => {
      this.showNotification('Error loading image');
    };
  }

  /**
   * Copy the image URL to clipboard
   */
  public copyLink(): void {
    navigator.clipboard.writeText(this.imageElement.src)
      .then(() => {
        this.showNotification('Image SVG link copied');
      })
      .catch(err => {
        console.error('Error copying link to clipboard:', err);
        this.showNotification('Error copying link');
      });
  }

  /**
   * Show a notification message
   * @param message The message to show
   */
  private showNotification(message: string): void {
    this.notificationElement.textContent = message;
    this.notificationElement.style.display = 'block';

    if (this._notificationTimeout !== null) {
      window.clearTimeout(this._notificationTimeout);
    }

    this._notificationTimeout = window.setTimeout(() => {
      this.notificationElement.style.display = 'none';
      this._notificationTimeout = null;
    }, 3000);
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-image-helper')) {
  customElements.define('qds-image-helper', QdsImageHelper);
}
